import parse from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';
import React from 'react';
import YouTube from 'react-youtube';
import ArticleImageBlock from '../components/ArticleMarkup/ArticleImageBlock';
import * as style from '../templates/Article/Article.module.scss';
import ArticleList from '../components/ArticleMarkup/ArticleList';
import ArticleQuote from '../components/ArticleMarkup/ArticleQuote';
import {
  clearAllTagsFromString,
  getYoutubeVideoIdFromUrl,
  valueToBinaryLogic,
} from './index';
import ArticleNumberedHeading from '../components/ArticleMarkup/ArticleNumberedHeading';
import ArticleButtonGroup from '../components/ArticleMarkup/ArticleButtonGroup';
import ArticleButton from '../components/ArticleMarkup/ArticleButton';
import VideoPlayer from '../components/VideoPlayer';
import ArticleProductReviewBlock from '../components/ArticleMarkup/ArticlePoductReviewBlock';
import ArticleAccordion from '../components/ArticleMarkup/ArticleAccordion';
import ArticleCallOutIconList from '../components/ArticleMarkup/ArticleCallOutIconList';
import ArticleCallOutCardDeck from '../components/ArticleMarkup/ArticleCallOutCardDeck';
import ArticleConversationBlock from '../components/ArticleMarkup/ArticleConversationBlock';
import ArticleNumberedHeadingWithImage from '../components/ArticleMarkup/ArticleNumberedHeadingWithImage';
import ArticleImageQuoteBlock from '../components/ArticleMarkup/ArticleImageQuoteBlock';
import ArticlePeopleListBlock from '../components/ArticleMarkup/ArticlePeopleListBlock';
import ArticleDivider from '../components/ArticleMarkup/ArticleDivider';
import ArticleTableOfContents from '../components/ArticleMarkup/ArticleTableOfContents';
import { getLanguageFromField } from './sharedUtils';
import ArticleNumberedList from '../components/ArticleMarkup/ArticleNumberedList';
import { mapImageToCallOutBulletList } from './mapImageToCallOutBulletList';

const onAnchorLinkClick = (e) => {
  const clickedLink = e.currentTarget;
  const { hash } = clickedLink;

  if (!hash) return;

  const scrollToElement = document.getElementById(hash.substring(1));

  if (!scrollToElement) return;

  document
    .getElementsByTagName('html')[0]
    .scrollTo({ top: scrollToElement.offsetTop - 80, behavior: 'smooth' });
  // eslint-disable-next-line no-restricted-globals
  history.pushState(
    {},
    '',
    `${window.location.origin}${window.location.pathname}${hash}`
  );
};

/**
 * Parse block data to respective React components and elements
 * @param blocks - blocks from WordPress
 * @param locale - locale
 * @returns {[]} - array of React components
 */
const parseBlocks = (blocks, locale) => {
  const resComponentArray = [];

  for (let i = 0; i < blocks?.length; i += 1) {
    const block = blocks[i];

    switch (block.__typename) {
      /**
       * Text paragraphs <p>
       */
      case 'WpCoreParagraphBlock': {
        const paragraphTemp = parse(sanitizeHtml(block?.saveContent));
        const paragraphTempWithProps = React.cloneElement(paragraphTemp, {
          key: block?.order,
        });
        resComponentArray.push(paragraphTempWithProps);
        break;
      }

      /**
       * Image blocks
       * WpAcfCustomImageBlock instead of WpCoreImageBlock
       */
      case 'WpAcfCustomImageBlock': {
        const { CustomImage } = block;
        const {
          customImageBlock,
          customImageAttributionText,
          customImageTitleAndAltText,
          customImageWebsiteAttributionOption,
        } = CustomImage;

        const { customImageTitleTag, customImageAltText } =
          customImageTitleAndAltText;

        resComponentArray.push(
          <ArticleImageBlock
            key={block?.order}
            imgData={customImageBlock?.imgixImage?.gatsbyImageData}
            mimeType={customImageBlock?.mimeType}
            sourceUrl={customImageBlock?.sourceUrl}
            altText={
              customImageAltText ||
              customImageBlock?.altText ||
              customImageBlock?.slug
            }
            hoverTitle={
              customImageTitleTag ||
              customImageBlock?.title ||
              customImageBlock?.slug
            }
            authorName={customImageAttributionText}
            websiteName={
              customImageWebsiteAttributionOption
                ?.customImageWebsiteAttributionTextAndLink?.title
            }
            websiteUrl={
              customImageWebsiteAttributionOption
                ?.customImageWebsiteAttributionTextAndLink?.url
            }
            websiteUrlTarget={
              customImageWebsiteAttributionOption
                ?.customImageWebsiteAttributionTextAndLink?.target
            }
            isNoFollow={
              customImageWebsiteAttributionOption?.customImageWebsiteAttributionTextAndLinkMakeNoFollow
            }
            className={style.sectionOuterMarginTop}
          />
        );
        break;
      }
      /**
       * Heading text <h1>, <h2>, <h3>, <h4>, <h5>
       */
      case 'WpCoreHeadingBlock': {
        const tempHeading = parse(sanitizeHtml(block?.saveContent));
        const tempHeadingWithProps = React.cloneElement(tempHeading, {
          key: block?.order,
          id: block?.attributes?.anchor,
          style: { textAlign: block?.attributes?.textAlign },
          className: `${style.anchoredHeading} ${style.sectionOuterMarginTop}`,
        });
        resComponentArray.push(tempHeadingWithProps);
        break;
      }
      /**
       * List with icons-bullets ("check", "X", etc.)
       */
      case 'WpAcfSimpleIconListBlock':
        resComponentArray.push(
          <ArticleList
            key={block?.order}
            listItems={block?.SimpleIconList?.simpleIconLists}
          />
        );
        break;
      /**
       * List with number-bullets
       */
      case 'WpAcfNumberIconListBlock':
        resComponentArray.push(
          <ArticleNumberedList
            listItems={block.SimpleNumberedIconList?.simpleNumberedIconLists}
          />
        );
        break;
      /**
       * Quote block with black vertical stripe to the left.
       */
      case 'WpCoreQuoteBlock':
        resComponentArray.push(
          <ArticleQuote
            key={block?.order}
            text={clearAllTagsFromString(sanitizeHtml(block?.saveContent))}
          />
        );
        break;
      /**
       * Numbered heading with or w/o tags on bottom.
       * Full or short version.
       */
      case 'WpAcfNumberedHeadingBlock': {
        const { numberedHeading } = block;

        resComponentArray.push(
          <ArticleNumberedHeading
            key={block?.order}
            number={parseInt(numberedHeading?.numberedHeading, 10)}
            title={clearAllTagsFromString(
              numberedHeading?.numberedHeadingTitle
            )}
            titleTag={numberedHeading?.numberedHeadingTitleTag}
            awardsList={numberedHeading?.awardsList}
            id={numberedHeading?.numberedHeadingAnchorLinkId}
            variant="short"
            className={`${style.sectionOuterMarginTop} ${style.noSpacingBelow}`}
          />
        );
        break;
      }
      /**
       * Button with or w/o link. WpAcfCustomButtonBlock instead of WpCoreButtonsBlock.
       */
      case 'WpAcfCustomButtonBlock': {
        const customButton = block?.customButton?.customButton;
        resComponentArray.push(
          <ArticleButtonGroup key={block?.order}>
            <ArticleButton
              text={customButton?.customButtonTextAndLink?.title}
              variant={customButton?.customButtonStyle}
              href={customButton?.customButtonTextAndLink?.url}
              isNoFollow={customButton?.customButtonLinkMakeNoFollow}
              hrefTarget={customButton?.customButtonTextAndLink?.target}
            />
          </ArticleButtonGroup>
        );
        break;
      }
      /**
       * Video (self-hosted)
       */
      case 'WpCoreVideoBlock': {
        const videoBlockAttributes = block?.attributes;

        resComponentArray.push(
          <VideoPlayer
            key={blocks?.order}
            autoplay={videoBlockAttributes?.autoplay}
            loop={videoBlockAttributes?.loop}
            controls={videoBlockAttributes?.controls}
            src={videoBlockAttributes?.src}
            muted={videoBlockAttributes?.muted}
            playsInline={videoBlockAttributes?.playsInline}
          />
        );
        break;
      }
      /**
       * Product review block
       */
      case 'WpAcfProductReviewBlock': {
        const { productReview } = block;

        resComponentArray.push(
          <ArticleProductReviewBlock
            key={block?.order}
            number={productReview?.number}
            title={productReview?.productName}
            titleTag={productReview?.productNameHeadingTag}
            imgData={productReview?.image?.imgixImage?.gatsbyImageData}
            altText={productReview?.image?.altText}
            productFeatures={productReview?.productFeatures}
            productReviewText={productReview?.productReviewText}
            pros={productReview?.pros}
            cons={productReview?.cons}
            button={productReview?.button}
            anchor={productReview?.individualProductReviewAnchorLinkId}
          />
        );
        break;
      }
      /**
       * Accordion block
       */
      case 'WpAcfAccordionBlock':
        resComponentArray.push(
          <ArticleAccordion
            key={block?.order}
            sections={block?.Accordion?.accordion}
          />
        );
        break;
      /**
       * Call out bullet list. Icon + heading + text as a list. Icon as a list bullet.
       */
      case 'WpAcfCallOutBulletListBlock':
        // eslint-disable-next-line no-case-declarations
        let imageBulletList = null;
        if (block?.ImageIconBulletList?.imageIconBulletPointList?.length > 0) {
          imageBulletList = mapImageToCallOutBulletList(
            block?.ImageIconBulletList?.imageIconBulletPointList
          );
        }
        resComponentArray.push(
          <ArticleCallOutIconList
            key={block?.order}
            listItems={
              imageBulletList || block?.CallOutBulletList?.iconBulletPointList
            }
          />
        );
        break;
      /**
       * Call out horizontal. Icon + heading + text as a card in a column. Cards are placed in a deck.
       */
      case 'WpAcfCallOutHorizontalBlock':
        resComponentArray.push(
          <ArticleCallOutCardDeck
            key={block?.order}
            cardsList={block?.CallOutHorizontal?.callOutBoxHorizontal}
          />
        );
        break;
      /**
       * Call out conversation block. Messages dialogue answering questions.
       */
      case 'WpAcfCallOutConversationBlock':
        resComponentArray.push(
          <ArticleConversationBlock
            key={block?.order}
            messagesQA={block?.CallOutConversation?.conversation}
          />
        );
        break;
      /**
       * Block that consist of numbered heading and custom image fields.
       */
      case 'WpAcfNumberedHeadingWithImageBlock': {
        const { CustomImage, numberedHeading } = block;
        const {
          customImageBlock,
          customImageAttributionText,
          customImageWebsiteAttributionOption,
        } = CustomImage;

        resComponentArray.push(
          <ArticleNumberedHeadingWithImage
            key={block?.order}
            number={numberedHeading?.numberedHeading}
            awardsList={numberedHeading?.awardsList}
            title={numberedHeading?.numberedHeadingTitle}
            titleTag={numberedHeading?.numberedHeadingTitleTag}
            anchor={numberedHeading?.numberedHeadingAnchorLinkId}
            imgData={customImageBlock?.imgixImage?.gatsbyImageData}
            altText={customImageBlock?.altText || customImageBlock?.slug}
            hoverTitle={customImageBlock?.title || customImageBlock?.slug}
            authorName={customImageAttributionText}
            websiteName={
              customImageWebsiteAttributionOption
                ?.customImageWebsiteAttributionTextAndLink?.title
            }
            websiteUrl={
              customImageWebsiteAttributionOption
                ?.customImageWebsiteAttributionTextAndLink?.url
            }
            websiteUrlTarget={
              customImageWebsiteAttributionOption
                ?.customImageWebsiteAttributionTextAndLink?.target
            }
            isNoFollow={
              customImageWebsiteAttributionOption?.customImageWebsiteAttributionTextAndLinkMakeNoFollow
            }
            className={style.sectionOuterMarginTop}
          />
        );
        break;
      }
      /**
       * Quote block with author's image, name nad occupation above the quote
       */
      case 'WpAcfImageQuoteBlock': {
        const imageQuoteName = block?.ImageQuote?.imageQuote?.imageQuoteName;
        resComponentArray.push(
          <ArticleImageQuoteBlock
            key={block?.order}
            text={imageQuoteName?.imageQuoteNameQuoteText}
            authorName={imageQuoteName?.imageQuoteNameText}
            authorOccupation={imageQuoteName?.imageQuoteNameTitle}
            authorImageData={
              imageQuoteName?.imageQuoteNameImage?.imgixImage?.gatsbyImageData
            }
            linkUrl={imageQuoteName?.imageQuoteNameTitleLinkUrl}
            isNewTab={imageQuoteName?.imageQuoteNameTitleLinkUrlOpenNewTab}
            isNoFollow={imageQuoteName?.imageQuoteNameTitleLinkUrlMakeNoFollow}
          />
        );
        break;
      }
      /**
       * List of people with or without links
       */
      case 'WpAcfPeopleListBlock':
        resComponentArray.push(
          <ArticlePeopleListBlock
            key={block?.order}
            peopleList={block?.PeopleList?.peopleList}
            enableSlider={block?.PeopleList?.peopleListEnableSlider}
          />
        );
        break;
      /**
       * Divider / Separator block (<hr>)
       */
      case 'WpCoreSeparatorBlock':
        resComponentArray.push(<ArticleDivider key={block?.order} />);
        break;
      /**
       * Table of contents block
       */
      case 'WpAcfTableOfContentsBlock':
        resComponentArray.push(
          <ArticleTableOfContents
            key={block?.order}
            anchorsList={block?.TableOfContents?.tableOfContents}
            onAnchorLinkClick={onAnchorLinkClick}
          />
        );
        break;
      /**
       * Block to paste HTML as is (mainly used for Instagram embed)
       */
      case 'WpCoreHtmlBlock': {
        const { saveContent } = block;
        resComponentArray.push(
          <div
            className={`${style.coreHTMLBlock} ridestore-instagram-embed-custom`}
            key={block?.order}
          >
            {parse(saveContent, {
              replace: (domNode) => {
                if (domNode?.name === 'script')
                  return <div className="d-none" />;
              },
            })}
          </div>
        );
        break;
      }
      /**
       * Core List block to paste ul's / ol's as is
       */
      case 'WpCoreListBlock': {
        const tempList = parse(block?.saveContent);
        const tempHeadingWithProps = React.cloneElement(tempList, {
          key: block?.order,
          className: style.coreList,
        });
        resComponentArray.push(tempHeadingWithProps);
        break;
      }
      /**
       * Custom YouTube Video Block
       */
      case 'WpAcfCustomVideoBlock': {
        const { customVideo } = block;
        const {
          customVideoUrl,
          customVideoAllowFullscreen,
          customVideoAutoplay,
          customVideoKeyboardControls,
          customVideoHideYtLogo,
          customVideoControls,
          customVideoLoop,
          customVideoMuted,
        } = customVideo;

        const videoId = getYoutubeVideoIdFromUrl(customVideoUrl);

        const options = {
          height: '100%',
          width: '100%',
          // Extra
          playerVars: {
            autoplay: valueToBinaryLogic(customVideoAutoplay),
            controls: valueToBinaryLogic(customVideoControls),
            disablekb: valueToBinaryLogic(!customVideoKeyboardControls),
            fs: valueToBinaryLogic(customVideoAllowFullscreen),
            hl: getLanguageFromField('locale', locale).i18nName,
            loop: valueToBinaryLogic(customVideoLoop),
            playlist: customVideoLoop ? videoId : null,
            modestbranding: valueToBinaryLogic(customVideoHideYtLogo), // hide YouTube logo
            mute: valueToBinaryLogic(customVideoMuted),
          },
        };

        resComponentArray.push(
          <div className="ytPlayerWrapper" key={block?.order}>
            <YouTube videoId={videoId} opts={options} className="ytPlayer" />
          </div>
        );
        break;
      }
      case 'WpCoreTableBlock': {
        const table = block?.attributes?.body;

        let tableHTML = '';

        for (let rowIdx = 0; rowIdx < table?.length ?? 0; rowIdx += 1) {
          const row = table[rowIdx]?.cells;

          tableHTML += '<tr>';
          for (let colIdx = 0; colIdx < row?.length ?? 0; colIdx += 1) {
            const col = row[colIdx];
            tableHTML += `<td style="text-align: ${col?.align ?? 'left'}">${
              col?.content
            }</td>`;
          }
          tableHTML += '</tr>';
        }

        const tableElement = (
          <div>
            <table className={style.articleCoreTable}>{parse(tableHTML)}</table>
          </div>
        );

        resComponentArray.push(tableElement);
        break;
      }
      default:
        break;
    }
  }

  return resComponentArray;
};

export default parseBlocks;
